import type { ConnectivityStateFE, DeviationSeverityFE, MrmStateFE, VehicleFE } from 'domain/vehicle';
import { isVehicleOffline } from 'helper/connectivity';
import { getTopCriticalSeverity } from 'helper/severityRank';
import { flattenFeDeviations } from 'helper/vehicleV2';

export type VehicleStatusLevel = 'fatal' | 'error' | 'warning' | 'offline' | 'online' | 'base';

const MRM_IN_PROCESS_STATES: readonly MrmStateFE[] = ['EXECUTING_MRM', 'MRC', 'RELEASING_MRC'];

export const resolveStatusLevel = ({
    severity,
    connectivityState,
    mrmState,
}: {
    severity: DeviationSeverityFE | undefined;
    connectivityState: ConnectivityStateFE | null | undefined;
    mrmState?: MrmStateFE;
}): VehicleStatusLevel => {
    if (severity === 'FATAL') {
        return 'fatal';
    }
    if (severity === 'ERROR') {
        return 'error';
    }
    if (severity === 'WARNING') {
        return 'warning';
    }

    if (mrmState && MRM_IN_PROCESS_STATES.includes(mrmState)) {
        return 'base';
    }

    return connectivityState !== 'ONLINE' ? 'offline' : 'online';
};

export const getVehicleStatusLevel = (vehicle: VehicleFE): VehicleStatusLevel => {
    const activeDeviations = flattenFeDeviations(vehicle).filter(d => d.state !== 'RESOLVED');
    const severity = getTopCriticalSeverity(activeDeviations);

    if (severity) {
        return resolveStatusLevel({ severity, connectivityState: undefined });
    }

    const mrmState = vehicle.mrm.value?.value?.state;
    if (mrmState && MRM_IN_PROCESS_STATES.includes(mrmState)) {
        return 'base';
    }

    return isVehicleOffline(vehicle) ? 'offline' : 'online';
};

export const vehicleStatusModifier = (
    level: VehicleStatusLevel,
    opts?: { foldOnlineToBase?: boolean }
): VehicleStatusLevel | undefined => {
    if (level === 'base') {
        return undefined;
    }
    if (level === 'online' && opts?.foldOnlineToBase) {
        return undefined;
    }
    return level;
};
